import { Injectable, inject } from '@angular/core';
import { APP_BASE_HREF, PathLocationStrategy, PlatformLocation } from '@angular/common';
import { ORDERED_QUESTION_B2B_CONFIG } from './core/service/questions/config/questions-config-b2b';
import { ORDERED_QUESTION_B2C_CONFIG } from './core/service/questions/config/questions-config-b2c';
import { ClientType, TypeDetectorService, urlPrefixB2B_B2C } from './core/service/type-detector.service';
import { I18nService } from './core/service/i18n/i18n.service';

export enum Languages {
  fr = 'fr',
  nl = 'nl',
  en = 'en',
}

@Injectable()
export class TranslateLocationStrategy extends PathLocationStrategy {
  private readonly i18nService = inject(I18nService);

  constructor() {
    const _platformLocation = inject(PlatformLocation);
    const href = inject(APP_BASE_HREF, { optional: true });

    super(_platformLocation, href);
  }

  prepareExternalUrl(internal: string): string {
    if (internal === '/') {
      return internal;
    }
    const b2bPrefixes = {
      fr: `/fr/${urlPrefixB2B_B2C.b2b.fr}/produits-et-services/my-business/notre-offre-energie/calculez-votre-prix`,
      nl: `/nl/${urlPrefixB2B_B2C.b2b.nl}/producten-en-diensten/my-business/ons-energieaanbod/bereken-jouw-prijs`,
      en: `/en/${urlPrefixB2B_B2C.b2b.en}/products-and-services/my-business/our-energy-offer/calculate-your-price`,
    };
    const b2cPrefixes = {
      fr: `/fr/${urlPrefixB2B_B2C.b2c.fr}/produits-et-services/my-home/electricite-et-gaz/offre-electricite-et-gaz/calculez-votre-prix`,
      nl: `/nl/${urlPrefixB2B_B2C.b2c.nl}/producten-en-diensten/my-home/elektriciteit-en-aardgas/aanbod-electriciteit-en-aardgas/bereken-jouw-prijs`,
      en: `/en/${urlPrefixB2B_B2C.b2c.en}/products-and-services/my-home/electricity-and-gas/electricity-and-gas-offer/calculate-your-price`,
    };
    const prefixToUse = TypeDetectorService.detectType() === ClientType.B2B ? b2bPrefixes : b2cPrefixes;
    const prefix = prefixToUse[this.i18nService.language]
      ? prefixToUse[this.i18nService.language]
      : prefixToUse[Languages.fr];
    const routeWithoutPrefix = internal.includes(prefix) ? internal.replace(prefix, '') : internal;
    return prefix + '/' + this.translateRoute(this.i18nService.language, decodeURIComponent(routeWithoutPrefix));
  }

  private translateRoute(lang: string, route: string): string {
    const queryParamsStartIndex = decodeURIComponent(route).indexOf('?');
    const routeWithoutParams =
      queryParamsStartIndex !== -1 ? decodeURIComponent(route.substring(0, queryParamsStartIndex)) : route;

    const routeTranslatePerUri = new Map<string, { fr: string; nl: string; en: string }>();
    (TypeDetectorService.detectType() === ClientType.B2B
      ? ORDERED_QUESTION_B2B_CONFIG
      : ORDERED_QUESTION_B2C_CONFIG
    ).forEach((question) => {
      routeTranslatePerUri.set(question.getUrl(), {
        fr: question.getFrTranslatedUrl(),
        nl: question.getNlTranslatedUrl(),
        en: question.getEnTranslatedUrl(),
      });
      routeTranslatePerUri.set('/' + question.getFrTranslatedUrl(), {
        // if user refresh page with translated path
        fr: question.getFrTranslatedUrl(),
        nl: question.getNlTranslatedUrl(),
        en: question.getEnTranslatedUrl(),
      });
      routeTranslatePerUri.set('/' + question.getNlTranslatedUrl(), {
        // if user refresh page with translated path
        fr: question.getFrTranslatedUrl(),
        nl: question.getNlTranslatedUrl(),
        en: question.getEnTranslatedUrl(),
      });
    });
    routeTranslatePerUri.set('/profil-not-found', {
      nl: 'profil-not-found',
      fr: 'profil-not-found',
      en: 'profile-not-found',
    });
    routeTranslatePerUri.set('/error', { nl: 'error', fr: 'error', en: 'error' });
    if (!routeTranslatePerUri.get(routeWithoutParams)) {
      return decodeURIComponent(route);
    }
    return (
      routeTranslatePerUri.get(routeWithoutParams)[lang] +
      (queryParamsStartIndex !== -1
        ? route.substring(
            queryParamsStartIndex,
            route.lastIndexOf('?') !== queryParamsStartIndex ? route.lastIndexOf('?') : route.length,
          )
        : '')
    );
  }
}
